import { Component, inject, signal } from '@angular/core';
import {
  InspectionDetailDto,
  InspectionItemDto,
  InspectionService, InspectionFilterDto, InspectionMergeDto, RegionItemDto, TeamItemDto,
} from '@artemis-software/wr-api';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { NbDateService } from '@nebular/theme';
import { currentTenantId } from '@/utils/admin-utils';
import { toSelectedRange } from '@/utils/filter-range-utils';
import StatusEnum = InspectionMergeDto.StatusEnum;

@Component({
  standalone: false,
  selector: 'wr-external-inspection-overview-page',
  templateUrl: './external-inspection-overview-page.component.html',
  styleUrls: ['./external-inspection-overview-page.component.scss'],
})
export class ExternalInspectionOverviewPageComponent {

  private readonly router = inject(Router);
  private readonly inspectionService = inject(InspectionService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly dateService = inject<NbDateService<Date>>(NbDateService);

  tenantId = currentTenantId();

  readonly loadInspections = (filter: InspectionFilterDto) => this.inspectionService.findAll(this.prepareFilter(filter));
  readonly getCount = (filter: InspectionFilterDto) => this.inspectionService.getCount(this.prepareFilter(filter));

  readonly prepareFilter = (filter: InspectionFilterDto): InspectionFilterDto => {
    return {
      ...filter,
      selectedRange: toSelectedRange(filter.selectedRange),
      tenantId: this.tenantId(),
      status: StatusEnum.Sent,
    };
  };

  form!: FormGroup;

  constructor() {
    const startOfMonth = this.dateService.getMonthStart(this.dateService.today());
    const endOfMonth = this.dateService.getMonthEnd(this.dateService.addMonth(startOfMonth, 1));

    this.form = this.formBuilder.group({
      fullTextSearch: this.formBuilder.control(''),
      tenantId: this.formBuilder.control(undefined),
      continuationNumber: this.formBuilder.control(undefined),
      building: this.formBuilder.control(undefined),
      buildingId: this.formBuilder.control(null),
      status: this.formBuilder.control(InspectionDetailDto.StatusEnum.None),
      selectedRange: new FormControl({
        start: startOfMonth,
        end: endOfMonth,
      }),
      region: this.formBuilder.control<RegionItemDto | null>(null),
      regionId: this.formBuilder.control<string | null>(null),
      team: this.formBuilder.control<TeamItemDto | null>(null),
      teamId: this.formBuilder.control<string | null>(null),
      condition: this.formBuilder.control(undefined),
      isPropertyManager: this.formBuilder.control(true),
    });

    this.form.get('region')?.valueChanges.subscribe((region) => {
      if (region) {
        this.form.get('regionId')?.setValue(region.id);
      } else {
        this.form.get('regionId')?.setValue(null);
      }
    });

    this.form.get('team')?.valueChanges.subscribe((team) => {
      if (team) {
        this.form.get('teamId')?.setValue(team.id);
      } else {
        this.form.get('teamId')?.setValue(null);
      }
    });
  }

  navigateToInspection(entry: InspectionItemDto): void {
    this.router.navigate(['inspection', entry.id]);
  }

  protected readonly signal = signal;
}
